// Generated by OrchestKit Claude Plugin
// Created: 2026-09-12

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { useDiffere } from '../../composants/use-differe.ts';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, DateHeure, Erreur, formaterDateHeure } from '../../composants/communs.tsx';
import {
  BoutonImprimer,
  DataTable,
  Montant,
  Nombre,
  Sheet,
  formaterFcfa,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireRetour } from './FormulaireRetour.tsx';
import { REMBOURSEMENTS, type Retour } from './types.ts';

/**
 * Retours de marchandise — Q-039 à Q-042.
 *
 * Deux boutiques apparaissent parce qu'elles peuvent différer : le client
 * rend où il se présente, mais le poids financier reste à la boutique qui a
 * vendu (Q-041). Confondre les deux ferait porter la reprise à la mauvaise
 * caisse.
 */
export function Retours(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId, peut } = useSession();
  const [saisie, setSaisie] = useState(false);
  const consolide = boutiqueId === null;

  // Recherche libre (numéro de retour, de vente, ou nom/prénom du client) et
  // période — mêmes filtres que la page Ventes, portés par le GET /retours
  // déjà étendu côté API.
  const [texteRecherche, setTexteRecherche] = useState('');
  const recherche = useDiffere(texteRecherche.trim(), 350);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  const { data, isPending, error } = useQuery({
    queryKey: ['retours', boutiqueId, recherche, dateDebut, dateFin],
    queryFn: () => {
      const parametres = new URLSearchParams();
      if (boutiqueId !== null) parametres.set('boutique_id', boutiqueId);
      if (recherche.length >= 2) parametres.set('recherche', recherche);
      if (dateDebut !== '') parametres.set('date_debut', dateDebut);
      if (dateFin !== '') parametres.set('date_fin', dateFin);
      return appeler<{ donnees: Retour[] }>(`/retours?${parametres.toString()}`);
    },
    select: (reponse) => reponse.donnees,
  });

  return (
    <>
      <Entete
        titre="Retours"
        sous="Marchandise rendue par les clients : reprise du stock et remboursement (Q-039 à Q-042)."
        actions={
          <label>
            <span className="visuellement-cache">Rechercher</span>
            <input
              type="search"
              placeholder="N° de retour, de vente, ou client"
              value={texteRecherche}
              onChange={(evenement) => {
                setTexteRecherche(evenement.target.value);
              }}
            />
          </label>
        }
      />

      <div className="ligne" style={{ marginBottom: 12 }}>
        <div className="champ" style={{ margin: 0 }}>
          <label htmlFor="retours-date-debut">Depuis</label>
          <input
            id="retours-date-debut"
            type="date"
            value={dateDebut}
            max={dateFin === '' ? undefined : dateFin}
            onChange={(evenement) => {
              setDateDebut(evenement.target.value);
            }}
          />
        </div>
        <div className="champ" style={{ margin: 0 }}>
          <label htmlFor="retours-date-fin">Jusqu’au</label>
          <input
            id="retours-date-fin"
            type="date"
            value={dateFin}
            min={dateDebut === '' ? undefined : dateDebut}
            onChange={(evenement) => {
              setDateFin(evenement.target.value);
            }}
          />
        </div>
        {(dateDebut !== '' || dateFin !== '') && (
          <button
            type="button"
            className="bouton--discret"
            style={{ alignSelf: 'flex-end' }}
            onClick={() => {
              setDateDebut('');
              setDateFin('');
            }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      <Erreur erreur={error} />

      <div className="pile">
        {peut('retour.creer') &&
          (saisie ? (
            <FormulaireRetour
              onFerme={() => {
                setSaisie(false);
              }}
            />
          ) : (
            <div>
              <button
                type="button"
                onClick={() => {
                  setSaisie(true);
                }}
              >
                Nouveau retour
              </button>
            </div>
          ))}

        {isPending ? (
          <Chargement />
        ) : (
          <Sheet
            action={
              (data ?? []).length > 0 ? (
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Retours',
                        colonnes: colonnesRetours(consolide),
                        lignes: data ?? [],
                        nomFichier: 'retours',
                      }),
                    );
                  }}
                />
              ) : undefined
            }
          >
            <DataTable
              lignes={data ?? []}
              cleDe={(retour: Retour) => retour.id}
              vide="Aucun retour enregistré."
              colonnes={colonnesRetours(consolide)}
            />
          </Sheet>
        )}
      </div>
    </>
  );
}

function nomClient(retour: Retour): string | null {
  const morceaux = [retour.client_nom, retour.client_prenom].filter(
    (morceau): morceau is string => morceau !== null && morceau !== '',
  );
  return morceaux.length === 0 ? null : morceaux.join(' ');
}

function colonnesRetours(consolide: boolean): Colonne<Retour>[] {
  return [
    {
      cle: 'numero',
      entete: 'N°',
      rendu: (retour: Retour) => (
        <Link to={`/retours/${retour.id}`} className="mono">
          {retour.numero}
        </Link>
      ),
      texte: (retour) => retour.numero,
    },
    {
      cle: 'date',
      entete: 'Date',
      rendu: (retour: Retour) => <DateHeure valeur={retour.horodatage_serveur} />,
      texte: (retour) => formaterDateHeure(retour.horodatage_serveur),
    },
    {
      cle: 'vente',
      entete: 'Vente',
      rendu: (retour: Retour) => <span className="mono">{retour.vente_numero}</span>,
      texte: (retour) => retour.vente_numero,
    },
    // En vue consolidée seulement : sur une boutique unique, ces colonnes
    // répéteraient la même valeur à chaque ligne.
    ...(consolide
      ? [
          {
            cle: 'boutique',
            entete: 'Rendu à',
            rendu: (retour: Retour) => retour.boutique,
            texte: (retour: Retour) => retour.boutique,
          },
          {
            cle: 'origine',
            entete: 'Vendu par',
            rendu: (retour: Retour) => retour.boutique_origine,
            texte: (retour: Retour) => retour.boutique_origine,
          },
        ]
      : []),
    {
      cle: 'client',
      entete: 'Client',
      rendu: (retour: Retour) => nomClient(retour) ?? '—',
      texte: (retour) => nomClient(retour) ?? '—',
    },
    {
      cle: 'articles',
      entete: 'Articles',
      numerique: true,
      rendu: (retour: Retour) => (
        <>
          <Nombre valeur={retour.articles} />
          {retour.articles_rebut > 0 && <span> dont {retour.articles_rebut} au rebut</span>}
        </>
      ),
      texte: (retour) =>
        retour.articles_rebut > 0
          ? `${String(retour.articles)} (dont ${String(retour.articles_rebut)} au rebut)`
          : String(retour.articles),
    },
    {
      cle: 'montant',
      entete: 'Montant',
      numerique: true,
      rendu: (retour: Retour) => <Montant valeur={retour.montant} />,
      texte: (retour) => formaterFcfa(retour.montant),
    },
    {
      cle: 'remboursement',
      entete: 'Remboursement',
      rendu: (retour: Retour) => REMBOURSEMENTS[retour.remboursement],
      texte: (retour) => REMBOURSEMENTS[retour.remboursement],
    },
    {
      cle: 'fenetre',
      entete: 'Fenêtre',
      // Une dérogation doit se VOIR dans l'historique : c'est le seul
      // endroit où un gérant peut remarquer qu'elle devient une habitude.
      rendu: (retour: Retour) => (retour.hors_fenetre ? 'Dérogation' : '—'),
      texte: (retour) => (retour.hors_fenetre ? 'Dérogation' : '—'),
    },
    {
      cle: 'par',
      entete: 'Enregistré par',
      rendu: (retour: Retour) => retour.enregistre_par,
      texte: (retour) => retour.enregistre_par,
    },
  ];
}
